import React from 'react';
import { UserProfile, SpendingStage } from '../../types/advanced-retirement';
import FormattedInput from '../FormattedInput';

interface SpendingLifestyleSectionProps {
  userProfile: UserProfile;
  handleInputChange: (field: keyof UserProfile, value: any) => void;
  setGuidanceTitle?: (title: string) => void;
  setGuidanceContent?: (content: string) => void;
  setShowGuidanceModal?: (show: boolean) => void;
}

const SpendingLifestyleSection: React.FC<SpendingLifestyleSectionProps> = ({
  userProfile,
  handleInputChange,
  setGuidanceTitle,
  setGuidanceContent,
  setShowGuidanceModal
}) => {
  const addSpendingStage = (isPersonal = false) => {
    const currentStages = isPersonal ? userProfile.personalSpendingStages || [] : userProfile.sharedSpendingStages;
    const newStage: SpendingStage = {
      stage: currentStages.length + 1,
      startingAge: currentStages.length > 0 ? currentStages[currentStages.length - 1].endingAge + 1 : userProfile.age,
      endingAge: currentStages.length > 0 ? currentStages[currentStages.length - 1].endingAge + 1 : userProfile.age + 19,
      amount: currentStages.length > 0 ? currentStages[currentStages.length - 1].amount : 60000
    };
    
    if (isPersonal) {
      const updatedStages = [...currentStages, newStage];
      handleInputChange('personalSpendingStages', updatedStages);
    } else {
      const updatedStages = [...currentStages, newStage];
      handleInputChange('sharedSpendingStages', updatedStages);
    }
  };

  const removeSpendingStage = (stageNumber: number, isPersonal = false) => {
    const currentStages = isPersonal ? userProfile.personalSpendingStages || [] : userProfile.sharedSpendingStages;
    if (currentStages.length <= 1) return; // Keep at least one stage
    
    const updatedStages = currentStages
      .filter(stage => stage.stage !== stageNumber)
      .map((stage, index) => ({ ...stage, stage: index + 1 }));
    
    if (isPersonal) {
      handleInputChange('personalSpendingStages', updatedStages);
    } else {
      handleInputChange('sharedSpendingStages', updatedStages);
    }
  };

  const updateSpendingStage = (stageNumber: number, field: keyof SpendingStage, value: any, isPersonal = false) => {
    const currentStages = isPersonal ? userProfile.personalSpendingStages || [] : userProfile.sharedSpendingStages;
    const updatedStages = currentStages.map(stage => 
      stage.stage === stageNumber ? { ...stage, [field]: value } : stage
    );
    
    if (isPersonal) {
      handleInputChange('personalSpendingStages', updatedStages);
    } else {
      handleInputChange('sharedSpendingStages', updatedStages);
    }
  };

  const renderSpendingTable = (stages: SpendingStage[], isPersonal = false) => (
    <div className="spending-table">
      <div className="table-header">
        <div className="table-cell">Stage</div>
        <div className="table-cell">Starting Age</div>
        <div className="table-cell">Ending Age</div>
        <div className="table-cell">Amount</div>
        <div className="table-cell">Actions</div>
      </div>
      {stages.map((stage) => (
        <div key={stage.stage} className="table-row">
          <div className="table-cell">
            <span className="stage-label">Stage {stage.stage}</span>
          </div>
          <div className="table-cell">
            <input
              type="number"
              value={stage.startingAge}
              onChange={(e) => updateSpendingStage(stage.stage, 'startingAge', parseInt(e.target.value), isPersonal)}
              min={userProfile.age}
              max={85}
            />
          </div>
          <div className="table-cell">
            <input
              type="number"
              value={stage.endingAge}
              onChange={(e) => updateSpendingStage(stage.stage, 'endingAge', parseInt(e.target.value), isPersonal)}
              min={stage.startingAge}
              max={85}
            />
          </div>
          <div className="table-cell">
            <FormattedInput
              value={stage.amount}
              onChange={(value) => updateSpendingStage(stage.stage, 'amount', value, isPersonal)}
              formatOptions={{ type: 'currency', decimalPlaces: 0 }}
            />
          </div>
          <div className="table-cell">
            <button
              type="button"
              className="remove-stage-btn"
              onClick={() => removeSpendingStage(stage.stage, isPersonal)}
              disabled={stages.length <= 1}
            >
              ✕
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="form-section">
      <h3>Spending and Lifestyle</h3>
      
      {/* Shared Spending Table */}
      <div>
        <h4>Shared Spending</h4>
        {renderSpendingTable(userProfile.sharedSpendingStages, false)}
        <button 
          type="button" 
          className="add-stage-btn"
          onClick={() => addSpendingStage(false)}
        >
          [Add Stage]
        </button>
      </div>

      {/* Personal Spending Table (Optional) */}
      <div className="form-group">
        <div className="checkbox-input-group">
          <input
            type="checkbox"
            checked={userProfile.personalSpendingEnabled || false}
            onChange={(e) => handleInputChange('personalSpendingEnabled', e.target.checked)}
          />
          <label>TABLE: My Spending (optional on/off)</label>
        </div>
        
        {userProfile.personalSpendingEnabled && (
          <>
            {renderSpendingTable(userProfile.personalSpendingStages || [], true)}
            <button 
              type="button" 
              className="add-stage-btn"
              onClick={() => addSpendingStage(true)}
            >
              [Add Stage]
            </button>
          </>
        )}
      </div>

      {/* Information Button */}
      <div className="info-section">
        <button 
          type="button" 
          className="guidance-btn"
          title="Spending & Lifestyle Information"
          onClick={() => { 
            if (setGuidanceTitle && setGuidanceContent && setShowGuidanceModal) {
              setGuidanceTitle('Spending & Lifestyle Information'); 
              setGuidanceContent('💰 <strong>Shared spending:</strong> Will continue until both parties have passed away<br/><br/>📈 <strong>Inflation:</strong> All numbers will be inflated over time<br/><br/>🎯 <strong>Retirement spending strategies:</strong><br/>• Essential expenses (housing, food, healthcare)<br/>• Discretionary spending (travel, hobbies, entertainment)<br/>• Emergency fund maintenance<br/>• Healthcare cost planning<br/>• Legacy planning considerations'); 
              setShowGuidanceModal(true); 
            }
          }}
        >
          ℹ
        </button>
      </div>
    </div>
  );
};

export default SpendingLifestyleSection; 